import React, { useState, useRef } from 'react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { TimeWheelPicker } from './TimeWheelPicker';

interface TimeInputWithPickerProps {
  label: string;
  value: string;
  onChange: (val: string) => void;
  onBlur?: () => void;
  required?: boolean;
  error?: string | null;
}

export function TimeInputWithPicker({
  label,
  value,
  onChange,
  onBlur,
  required,
  error,
}: TimeInputWithPickerProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const hasError = error && error.trim() !== '';

  const handleConfirm = (hhmm: string) => {
    onChange(hhmm);
    setIsPickerOpen(false);
    onBlur?.();
  };

  const handleClose = () => {
    setIsPickerOpen(false);
  };

  const togglePicker = () => {
    setIsPickerOpen(prev => !prev);
  };

  return (
    <div ref={anchorRef}>
      <label className="block mb-2">
        <span className="block mb-1.5 text-base font-semibold text-gray-600">
          {label}
          {required && <span className="text-danger-500 ml-1">*</span>}
        </span>
        <div className="relative">
          <input
            type="text"
            value={value}
            onChange={e => onChange(e.target.value)}
            onBlur={onBlur}
            maxLength={4}
            pattern="\d{4}"
            className={`w-full px-3 py-2.5 pr-9 border text-gray-900 text-lg outline-none transition-all bg-white
              ${isPickerOpen ? 'rounded-t-lg' : 'rounded-lg'}
              ${hasError
                ? 'border-danger-500 focus:border-danger-500 focus:ring-3 focus:ring-danger-500/10'
                : isPickerOpen
                  ? 'border-primary-500 border-b-gray-200 ring-3 ring-primary-500/10'
                  : 'border-gray-300 focus:border-primary-500 focus:ring-3 focus:ring-primary-500/10'
              }`}
          />
          <button
            type="button"
            onClick={togglePicker}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-500 transition-colors"
            tabIndex={-1}
            aria-label="Open time picker"
          >
            <AccessTimeIcon sx={{ fontSize: 20 }} />
          </button>
        </div>
      </label>
      {isPickerOpen && (
        <TimeWheelPicker
          value={value}
          anchorRef={anchorRef}
          onChange={onChange}
          onConfirm={handleConfirm}
          onClose={handleClose}
        />
      )}
    </div>
  );
}
